import { AppliedFilter } from '@/pages/Shipments/ControlTower/types';
import { buildDateLabel } from '@/pages/Shipments/Filters/utils/labels';
import { getUserDateFormat } from '@/common/utils/utils';

export type TooltipAppliedFilter = AppliedFilter & {
  id: string;
  name?: string;
  title?: string;
  filterBy?: string;
  groupTitle?: string;
  dateFilter?: string;
};

const toStartCase = (value = '') =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const getDateFilterKey = (item: TooltipAppliedFilter): string => {
  if (item.dateFilter) return item.dateFilter;
  if (item.filterBy?.includes('date')) return item.filterBy;
  return '';
};

export const getFilterDisplayName = (item: TooltipAppliedFilter): string => {
  const dateKey = getDateFilterKey(item);

  if (dateKey) {
    const normalizedMap: Record<string, string> = {
      scheduled_pickup_date: 'Pickup date',
      scheduled_delivery_date: 'Delivery date',
      pickup_date: 'Pickup date',
      delivery_date: 'Delivery date',
      created_date: 'Created date',
      created_at: 'Created date',
      updated_date: 'Updated date',
      updated_at: 'Updated date',
    };

    return normalizedMap[dateKey] || toStartCase(dateKey);
  }

  if (item.groupTitle && item.groupTitle.trim()) {
    return item.groupTitle;
  }

  return 'Other';
};

export const getFilterValue = (item: TooltipAppliedFilter): string => {
  const dateKey = getDateFilterKey(item);

  // Custom date-range start/end date_format so the tooltip reflects the user's preference
  if (
    dateKey &&
    (item.start || item.end) &&
    (!item.period || item.period === 'Custom')
  ) {
    const label = buildDateLabel(
      dateKey,
      (item.start as string) ?? null,
      (item.end as string) ?? null,
      getUserDateFormat()
    );
    return label.replace(/^[^:]+:\s*/, '');
  }

  const raw = item.name || item.title || String(item.id);

  if (dateKey) {
    return raw.replace(/^[^:]+:\s*/, '');
  }

  return raw;
};

export const buildSavedFilterTooltip = (
  appliedFilters: TooltipAppliedFilter[] = []
): string => {
  if (!appliedFilters.length) return '';

  const grouped = appliedFilters.reduce<Record<string, string[]>>(
    (acc, item) => {
      const key = getFilterDisplayName(item);

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(getFilterValue(item));
      return acc;
    },
    {}
  );

  return Object.entries(grouped)
    .map(([group, values]) => {
      if (values.length > 5) {
        const visible = values.slice(0, 4);
        const remaining = values.length - 4;
        return `${group}: ${visible.join(', ')} +${remaining}`;
      }

      return `${group}: ${values.join(', ')}`;
    })
    .join('\n');
};
